import VersusPage, {
  type VersusComparisonRow,
  type VersusFaq,
  type VersusStat,
  type VersusWorkflow,
} from "@/components/public/versus-page"
import type { SeoMeta } from "@/types"

const STATS: VersusStat[] = [
  {
    value: "60s",
    label: "listing to video",
    detail: "Not a retouch turnaround. A finished, branded video ad ready to post.",
  },
  {
    value: "\u00A3499",
    label: "all-in per month",
    detail: "One flat fee, fully managed. No per-asset pricing, no nickel-and-diming per listing.",
  },
  {
    value: "4",
    label: "channels published",
    detail: "Every render posts to Instagram, Facebook, TikTok and YouTube from one click.",
  },
  {
    value: "0",
    label: "briefs to write",
    detail: "We read the listing. You don't have to write a brief or a shot list.",
  },
]

const WORKFLOW: VersusWorkflow = {
  competitorSteps: [
    "Upload photos and write a brief",
    "Pick services per image: retouch, decluttering, sky swap, floorplan",
    "Wait for turnaround (often overnight)",
    "Review and request amends",
    "Download final assets",
    "Drop them into Rightmove / Zoopla",
    "Still need a video? Go somewhere else",
  ],
  competitorTotal: "Hours + next-day wait",
  quiterightSteps: [
    "Paste the listing URL",
    "QuiteRight builds the video and captions automatically",
    "Publish to every social channel in one click",
  ],
  quiterightTotal: "60 seconds",
}

const COMPARISON: VersusComparisonRow[] = [
  { label: "Primary output", competitor: "Retouched stills + floorplans", quiteright: "Video ads + social captions", highlight: true },
  { label: "Video content", competitor: "Virtual staging or slideshow add-on", quiteright: "Every listing, purpose-built", highlight: true },
  { label: "Turnaround", competitor: "Often next-day per service", quiteright: "60 seconds per listing", highlight: true },
  { label: "Scripts and captions", competitor: "Not included", quiteright: "Auto-written per property" },
  { label: "Voiceover", competitor: "Not included", quiteright: "AI voiceover, UK voices", highlight: true },
  { label: "Social publishing", competitor: "Manual", quiteright: "One click to IG, FB, TikTok, YouTube" },
  { label: "Pricing model", competitor: "Per-image, per-service", quiteright: "Flat \u00A3499/month, unlimited output", highlight: true },
  { label: "Brand consistency", competitor: "Visual style per editor", quiteright: "Same template engine every time" },
  { label: "Best use case", competitor: "One-off premium stills", quiteright: "High-volume ongoing video marketing" },
]

const FAQ: VersusFaq[] = [
  {
    q: "Is QuiteRight replacing BoxBrownie?",
    a: "Not exactly. BoxBrownie is primarily a photo enhancement and floorplan service \u2014 brilliant at retouching, sky swaps, virtual staging, and 2D floorplans. QuiteRight is a video marketing pipeline. Most agents will use both: BoxBrownie for premium portal stills, QuiteRight for social video marketing on every listing.",
  },
  {
    q: "Does BoxBrownie do video?",
    a: "They offer a slideshow video add-on, but it's a sequence of retouched stills with basic music \u2014 not a proper social-first video ad. QuiteRight produces purpose-built video ads: voiceover, on-screen captions, animated branding, correct aspect ratio per platform.",
  },
  {
    q: "Which is better for a small independent agent?",
    a: "If you want premium portal photography on a few listings a month, BoxBrownie. If you want every listing to have a professional video on Instagram, TikTok and Facebook without writing a brief, QuiteRight. Most high-performing independents use both.",
  },
  {
    q: "How does the pricing compare?",
    a: "BoxBrownie is per-image and per-service \u2014 very reasonable per asset, but costs scale with volume. QuiteRight is \u00A3499/month fully managed with unlimited video production, publishing, and scheduling. If you produce video for more than a handful of listings a month, QuiteRight is cheaper and much less admin.",
  },
  {
    q: "Can I feed BoxBrownie-retouched photos into QuiteRight?",
    a: "Yes. Import the listing URL once BoxBrownie's retouched photos are live on Rightmove or Zoopla and QuiteRight will pick them up. Or upload retouched images directly into the QuiteRight listing.",
  },
]

export default function VsBoxbrownie({ meta }: { meta: SeoMeta }) {
  return (
    <VersusPage
      meta={meta}
      competitorName="BoxBrownie"
      breadcrumbName="QuiteRight vs BoxBrownie"
      heroHeadline="Stills vs video. Different jobs, different tools"
      heroSubhead="BoxBrownie retouches photos and draws floorplans brilliantly. QuiteRight does something different \u2014 it turns every listing into a social-first video ad, with voiceover, captions and auto-publishing. Most agents use both."
      statsHeadline="Purpose-built for property video"
      statsSubhead="BoxBrownie makes your portal photos prettier. QuiteRight gets those properties on Reels, TikTok and Shorts \u2014 where buyers actually scroll."
      stats={STATS}
      workflowHeadline="One listing, two pipelines"
      workflow={WORKFLOW}
      comparison={COMPARISON}
      faq={FAQ}
      ctaHeadline="Give every listing a video ad, not just a retouch"
      ctaBody="BoxBrownie makes the photos cleaner. QuiteRight makes the phone scroll stop. Pick both."
      relatedKey="vs/boxbrownie"
    />
  )
}
